import { state as controllerState } from '../controllerState.js';
import { htmlEl } from './stationDisplays.js';
import { createFacilityHead } from './facilityUi.js';
import { buildMissionDetail, buildSkipMissionButton, isWaypointAtMission, missionStates } from './missionDetail.js';
import { itemDisplayName } from '../itemNames.js';
import { buildGroupViews, groupProgressLabel, isGroupRow } from './missionGroups.js';
import type { ControllerNetwork } from '../controllerNetwork.js';

type Options = {
  controls: HTMLDivElement | null;
  getControllerNetwork?: () => ControllerNetwork | null;
};

type ObjectiveState = 'done' | 'active' | 'pending';

export const createFacilityMissionsController = ({ controls, getControllerNetwork }: Options) => {
  let body: HTMLDivElement | null = null;
  let progressEl: HTMLElement | null = null;
  let lastSignature = '';
  let setCredits: ((value: number) => void) | null = null;
  const expanded = new Set<string>();
  const autoOpened = new Set<string>();

  const build = (): void => {
    const roomState = controllerState.lastRoomState;
    if (!body) return;
    body.innerHTML = '';
    const objectives = roomState?.missionObjectives ?? [];
    const activeIndex = roomState?.activeMissionIndex ?? 0;
    const states = missionStates(objectives, activeIndex);
    states.forEach((state, i) => {
      if (state === 'active' && !autoOpened.has(objectives[i].id)) { autoOpened.add(objectives[i].id); expanded.add(objectives[i].id); }
    });
    const navWaypoint = controllerState.lastSnapshot?.navWaypoint ?? undefined;

    const addDetail = (target: HTMLElement, index: number, titleEl: HTMLElement): void => {
      const objective = objectives[index];
      const open = expanded.has(objective.id);
      titleEl.classList.add('fc-expandable');
      titleEl.setAttribute('role', 'button');
      titleEl.setAttribute('aria-expanded', String(open));
      titleEl.tabIndex = 0;
      const toggle = (): void => { if (expanded.has(objective.id)) expanded.delete(objective.id); else expanded.add(objective.id); lastSignature = ''; updatePanel(); };
      titleEl.addEventListener('click', toggle);
      titleEl.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); toggle(); } });
      if (roomState?.devTools && !objective.optional && states[index] !== 'done') {
        target.append(buildSkipMissionButton(objective.id, (input) => getControllerNetwork?.()?.emitControl(input)));
      }
      if (open) target.append(buildMissionDetail({ objective, navWaypoint, setNavWaypoint: (wp) => getControllerNetwork?.()?.setNavWaypoint(wp) }));
    };

    const views = buildGroupViews(objectives, states);
    if (progressEl) {
      const mains = views.filter((v) => v.total > 0);
      const grouped = mains.some((v) => v.declared);
      progressEl.textContent = objectives.length === 0 ? ''
        : grouped ? `${mains.filter((v) => v.state === 'done').length} of ${mains.length} missions complete`
        : `${Math.min(activeIndex, objectives.length)} of ${objectives.length} objectives complete`;
    }

    if (objectives.length === 0) {
      body.append(htmlEl('div', 'facility-placeholder', 'No mission is loaded.'));
      return;
    }

    for (const view of views) {
      const indices = view.indices;
      const allDone = view.state === 'done';
      const hasActive = view.state === 'active';
      const groupState: ObjectiveState = view.state;
      const titleSpan = htmlEl('span', 'fc-mission-title', view.title);

      const card = htmlEl('section', `fc-mission ${groupState}`);
      const head = htmlEl('div', 'fc-mission-head');
      head.append(
        htmlEl('span', 'fc-mission-icon', allDone ? '✓' : hasActive ? '◉' : '○'),
        titleSpan,
        htmlEl('span', 'fc-mission-state', isGroupRow(view) && view.total > 0 ? groupProgressLabel(view).toLowerCase() : allDone ? 'complete' : hasActive ? 'in progress' : 'upcoming')
      );
      card.append(head);

      if (!isGroupRow(view)) {
        addDetail(card, indices[0], head);
      } else {
        if (view.summary) card.append(htmlEl('div', 'fc-hint', view.summary));
        if (view.reward.length > 0) card.append(htmlEl('div', 'fc-hint', `${allDone ? 'Rewarded' : 'Reward'}: ${view.reward.map((r) => `${r.quantity.toLocaleString()} x ${itemDisplayName(r.id)}`).join(', ')}`));
        const stages = htmlEl('ol', 'fc-stages');
        for (const index of indices) {
          const stage = objectives[index];
          const stageState: ObjectiveState = states[index];
          const item = htmlEl('li', `fc-stage ${stageState}`);
          const stageTitle = htmlEl('span', 'fc-stage-title', `${stageState === 'done' ? '✓' : stageState === 'active' ? '◉' : '○'} ${stage.title}`);
          item.append(stageTitle);
          addDetail(item, index, stageTitle);
          stages.append(item);
        }
        card.append(stages);
      }
      body.append(card);
    }
  };

  const signature = (): string => {
    const roomState = controllerState.lastRoomState;
    const wp = controllerState.lastSnapshot?.navWaypoint ?? undefined;
    return JSON.stringify([roomState?.missionObjectives, roomState?.activeMissionIndex, roomState?.devTools === true, [...expanded], roomState?.missionObjectives.map((o) => (isWaypointAtMission(o, wp) ? 1 : 0))]);
  };

  const render = (): void => {
    if (!controls) return;
    controls.innerHTML = '';
    controls.classList.add('facility-role-layout');

    const head = createFacilityHead('MISSION BOARD');
    setCredits = head.setCredits;
    progressEl = htmlEl('p', 'fc-hint');
    body = htmlEl('div', 'fc-list fc-missions');
    controls.append(head.root, progressEl, body);
    lastSignature = signature();
    build();
    updatePanel();
  };

  const updatePanel = (): void => {
    setCredits?.(controllerState.lastRoomState?.credits ?? 0);
    const next = signature();
    if (next !== lastSignature) {
      lastSignature = next;
      build();
    }
  };

  return { render, updatePanel };
};
